import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { NOTARY_KEYWORDS_TOP as NOTARY_KEYWORDS } from "@/content/notary-corpus/top.gen";
import type { FaqItem } from "@/content/notary-corpus/keywords";
import { NOTARY_FAQ_CATEGORIES, getCategoryDef } from "@/content/notary-corpus/faq-defs";

export const Route = createFileRoute("/en/notary/faq/$category")({
  loader: async ({ params }) => {
    const { getFaqsByCategoryEn } = await import("@/content/notary-corpus/categories-en");
    const def = getCategoryDef(params.category);
    if (!def) throw notFound();
    return { def, faqs: getFaqsByCategoryEn(def.slug) };
  },
  component: EnNotaryFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    const title = `${def.titleEn} — Notary Public FAQ (${faqs.length} answers, 2026)`;
    return {
      ...pageHeadForProvince({
        title,
        description: def.descEn,
        path: `/en/notary/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "th", path: `/notary/faq/${params.category}` }],
        keywords: NOTARY_KEYWORDS.slice(0, 60),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Notary FAQ", url: `${site}/en/notary/faq` },
              { name: def.titleEn, url: `${site}/en/notary/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/notary/faq/${params.category}`)) },
      ],
    };
  },
});

function EnNotaryFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/notary/faq" className="hover:underline">Notary FAQ</a> <span>/</span>{" "}
          <span>{def.titleEn}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleEn}</h1>
        <p className="text-muted-foreground mb-6">{def.descEn}</p>
        <p className="text-sm text-muted-foreground mb-8">
          <strong>{faqs.length}</strong> answered by Thai Notarial Services Attorneys, updated 2026.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {NOTARY_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/en/notary/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleEn}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: FaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              No questions yet — <a href="/en/contact" className="text-primary underline">ask our team</a>.
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Free consultation with a Thai Notarial Services Attorney</h2>
          <p className="text-sm text-muted-foreground mb-4">Response in 15 minutes (Mon-Sat 9:00-18:00 ICT)</p>
          <a href="/en/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">Contact us →</a>
        </section>
      
        <FaqSourceNote topic="notary" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}